import type { JiaxingCase } from "../types/journey";

/* ============================================================
   案例匹配：让第八幕的嘉兴案例与用户自己的问题更贴。
   做法：用"问题 + 三次回答"的文本去命中每条案例的关键词，
   再叠加 AI 判断的主题加成；分数相同则随机并避开上一条。
   ============================================================ */

/** 每条案例的匹配关键词（按案例内容与问题类型人工配置）。 */
export const CASE_KEYWORDS: Record<string, string[]> = {
  /* 从"路上送"，到"天上送"：农村物流 · 换个办法 */
  "JX-C-001": ["创新", "新方法", "办法", "效率", "物流", "快递", "乡村", "农村", "技术", "不一样", "老办法"],
  /* 实验室和生产线之间：科研 · 成果转化 */
  "JX-C-002": ["科研", "研究", "实验室", "成果", "转化", "学术", "论文", "专业", "读书", "学业", "学历"],
  /* 一个村做不了的事：合作 · 资源整合 */
  "JX-C-003": ["合作", "一起", "团队", "资源", "抱团", "联合", "伙伴", "独自", "单打独斗", "共同", "不配合", "配合", "协作", "意见不合", "分歧"],
  /* 城和乡同一张规划图：统筹 · 长远布局 */
  "JX-C-004": ["规划", "统筹", "布局", "长远", "整体", "城市", "融合", "系统"],
  /* 洪合毛衫：没有先天优势 · 长期积累 */
  "JX-F-001": ["产业", "积累", "起点", "条件", "耐心", "长期", "慢慢", "市场", "看不到结果", "熬", "没有优势"],
  /* 桐乡化纤：资源禀赋 · 升级转型 */
  "JX-F-002": ["资源", "禀赋", "优势", "转型", "升级", "短板", "从头", "差距"],
  /* 三星村桃林：乡村持续投入 */
  "JX-F-003": ["乡村", "农村", "家乡", "产业", "旅游", "一步一步", "持续", "一步步"],
  /* 红船故事年年讲：重复与传承的意义 */
  "JX-F-004": ["重复", "传承", "故事", "一遍", "继续做", "没人看见", "隐姓埋名"],
  /* 缪家村三双鞋：基层 · 走近群众 */
  "JX-D-001": ["群众", "基层", "百姓", "走访", "社区", "身边", "为民", "帮助别人", "奉献"],
  /* 徐家埭棒球场：返乡 · 说服与沟通 */
  "JX-D-002": ["回村", "返乡", "回家", "家乡", "家人", "父母", "支持", "反对", "不理解", "说服", "落地", "换工作", "辞职", "转行"],
  /* 小讲解员：青年 · 志愿讲述 */
  "JX-D-003": ["志愿", "讲解", "青年", "讲述", "传承", "学生", "孩子", "接力"],
  /* 青年青言青语：表达 · 传播 */
  "JX-D-004": ["表达", "分享", "传播", "文化", "说出来", "带动", "影响", "发朋友圈"],
};

/** 泛词：命中只记 1 分，避免"坚持/意义"这类常见词把匹配带偏。 */
const GENERIC_KEYWORDS = new Set([
  "坚持", "继续", "努力", "意义", "值得", "时间", "方法", "问题", "选择", "改变", "尝试",
  "帮助", "服务", "学习", "成长", "责任", "时代", "青春",
]);

/** AI 主题 → 案例三类主题（同时兼容直接传入案例类别名） */
const CASE_THEME_BY_TOPIC: Record<string, "innovation" | "struggle" | "dedication"> = {
  innovation: "innovation",
  collaboration: "innovation",
  uncertainty: "innovation",
  other: "innovation",
  persistence: "struggle",
  growth: "struggle",
  responsibility: "dedication",
  service: "dedication",
  /* 容错：直接给案例类别名时也成立 */
  struggle: "struggle",
  dedication: "dedication",
};

export function caseThemeForTopic(topic: string | undefined | null): "innovation" | "struggle" | "dedication" {
  return CASE_THEME_BY_TOPIC[topic ?? ""] ?? "innovation";
}

export interface CaseMatchInput {
  topic?: string | null;
  question?: string;
  firstAnswer?: string;
  secondAnswer?: string;
  thirdAnswer?: string;
  action?: string;
}

/** 打分：具体关键词 2 分、泛词 1 分，同类主题再加 1 分（主题只做微调，不压过关键词）。 */
export function scoreCase(item: JiaxingCase, text: string, preferredTheme: string): number {
  const keywords = CASE_KEYWORDS[item.id] ?? [];
  let score = 0;
  for (const keyword of keywords) {
    if (text.includes(keyword)) score += GENERIC_KEYWORDS.has(keyword) ? 1 : 2;
  }
  if (item.theme === preferredTheme) score += 1;
  return score;
}

/** 选出最贴的一条案例；同分随机，并尽量避开上一条。 */
export function pickCaseFor(
  list: JiaxingCase[],
  input: CaseMatchInput,
  random: () => number = Math.random,
  lastId?: string | null,
): JiaxingCase | null {
  if (list.length === 0) return null;
  const text = [input.question, input.firstAnswer, input.secondAnswer, input.thirdAnswer, input.action]
    .filter(Boolean)
    .join(" ");
  const preferred = caseThemeForTopic(input.topic);
  const scored = list.map((item) => ({ item, score: scoreCase(item, text, preferred) }));
  const best = Math.max(...scored.map((entry) => entry.score));
  const top = scored.filter((entry) => entry.score === best).map((entry) => entry.item);
  const pool = top.length > 1 && lastId ? top.filter((item) => item.id !== lastId) : top;
  const candidates = pool.length > 0 ? pool : top;
  return candidates[Math.floor(random() * candidates.length)] ?? candidates[0];
}
